import React, { useEffect, useState, useCallback, useRef } from "react";

import { emptyCart, fetchBasket, fetchWallet } from "services/basket.services";

import BasketCard from "./BasketCard";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import LoadingPage from "views/LoadingPage";
import { useTranslation } from "react-i18next";
import { useModal } from "context";
import { FormButton } from "reusableComponents";
import { deleteBooking } from "services/booking.services";
import {
  selectIsAuthenticated,
  selectAuthLoading,
} from "store/auth/selectors";
import { Login } from "modals";

import styles from "./styles.module.scss";

const POST_LOGIN_RETURN_URL_KEY = "post_login_return_url";
const BASKET_RETURN_PATH = "/basket";

const BasketPage = () => {
  const [basketData, setBasketData] = useState();
  const [walletData, setWalletData] = useState();

  const [loading, setLoading] = useState(null);
  const [generalError, setGeneralError] = useState(null);

  const loginModalOpenedRef = useRef(false);
  // Guards against stale responses overwriting newer state
  const basketRequestIdRef = useRef(0);

  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const { openModal } = useModal();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authLoading = useSelector(selectAuthLoading);
  const accessToken = useSelector((state) => state.auth.token);

  const getBasket = useCallback(async () => {
    const requestId = ++basketRequestIdRef.current;
    try {
      setLoading(true);
      const data = await fetchBasket(accessToken);

      // Discard stale responses — only the newest request may write state.
      if (requestId !== basketRequestIdRef.current) return null;

      setBasketData(data.data);
      return data.data;
    } catch (error) {
      if (requestId === basketRequestIdRef.current) {
        setGeneralError(error.message);
      }
      return null;
    } finally {
      if (requestId === basketRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [accessToken]);

  const getWallet = useCallback(async () => {
    try {
      const data = await fetchWallet(accessToken);
      setWalletData(data);
    } catch (error) {
      setGeneralError(error.message);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthenticated) {
      getBasket();
      getWallet();
    }
  }, [isAuthenticated, accessToken, getBasket, getWallet]);

  const openLoginModal = useCallback(() => {
    sessionStorage.setItem(POST_LOGIN_RETURN_URL_KEY, BASKET_RETURN_PATH);

    openModal({
      content: (
        <Login
          delegateRedirectToCaller
          onSuccess={() => {
            // Intentionally empty.
            //
            // The auth-driven effect above (depends on `isAuthenticated`)
            // fetches the basket as soon as the token propagates through
            // Redux. Calling getBasket() here as well would race that
            // call and could overwrite with an empty basket if the
            // second response arrives first during backend hydration.
            console.log(
              "[BasketPage] ✅ Login success — auth effect will fetch basket"
            );
          }}
        />
      ),
      title: t("modal.login.title"),
    });
  }, [openModal, t]);

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) return;
    if (loginModalOpenedRef.current) return;

    loginModalOpenedRef.current = true;
    openLoginModal();
  }, [authLoading, isAuthenticated, openLoginModal]);

  const handleReopenLogin = useCallback(() => {
    loginModalOpenedRef.current = true;
    openLoginModal();
  }, [openLoginModal]);

  const handleEdit = async (id, place) => {
    try {
      navigate(`/space/${place.id}/book?bookingId=${id}`);
    } catch (error) {
      console.error("Error navigating to booking page:", error);
    }
  };

  const handleDeleteBooking = async (id) => {
    try {
      await deleteBooking(id, accessToken);
      setBasketData((prevData) => ({
        ...prevData,
        basket_items: prevData.basket_items.filter((item) => item.id !== id),
      }));
    } catch (error) {
      console.error("Failed to delete booking:", error.message);
    }
  };

  const handleEmptyCart = () => {
    emptyCart(accessToken);
    setBasketData(null);
  };

  const basketItemsRender = () => {
    return basketData?.basket_items?.map((item) => (
      <BasketCard
        key={item.id}
        basketItem={item}
        handleEdit={(id, place) => handleEdit(id, place)}
        handleDeleteBooking={handleDeleteBooking}
      />
    ));
  };

  if (authLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return (
      <div className="pageContainer">
        <div className={styles.header}>
          <div className={styles.breadcrumb}>
            <a href="/">{t("home.title")} /</a>
            <p>{t("basket.title")}</p>
          </div>
        </div>

        <div className={styles.loginRequiredSection}>
          <div className={styles.noResultsSection}>
            <h1 className={styles.noResultsTitle}>
              {t("basket.loginRequiredTitle")}
            </h1>
            <p className={styles.noResultsText}>
              {t("basket.loginRequiredText")}
            </p>
            <div className={styles.loginRequiredActions}>
              <FormButton
                variant="submit"
                label={t("modal.login.login")}
                onClick={handleReopenLogin}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !basketData) {
    return <LoadingPage />;
  }

  if (basketData?.basket_items?.length === 0 || !basketData?.basket_items) {
    return (
      <div className={`${styles.noResultsSection} pageContainer`}>
        <div className={styles.breadcrumb}>
          <a href="/">{t("home.title")} /</a>
          <p>{t("basket.title")}</p>
        </div>
        <h1 className={styles.noResultsTitle}>{t("basket.noResultTitle")}</h1>
        <p
          className={styles.noResultsText}
          dangerouslySetInnerHTML={{ __html: t("basket.noResultText") }}
        ></p>
      </div>
    );
  }

  return (
    <div className="pageContainer">
      <div className={styles.header}>
        <div className={styles.breadcrumb}>
          <a href="/">{t("home.title")} /</a>
          <p>{t("basket.title")}</p>
        </div>
        <div className={styles.addSpaceBtnContainer}>
          <FormButton
            label={`+ ${t("basket.addAnotherSpace")}`}
            variant="secondary"
            onClick={() => {
              navigate("/search");
            }}
          />
        </div>
      </div>
      <div className={styles.basketPageBody}>
        <div>{basketItemsRender()}</div>
      </div>
    </div>
  );
};

export default BasketPage;